// The walkthroughs (in the style of logistics-workflow's reacttour/ tours). Each is a function of what's on the page, so
// a step never points at something that isn't there. They point at `data-tour` marks in the Game Builder's screens and at
// the tab buttons (#dashboard, #studio...). A step with `advanceOn` waits for that click (TourAutoAdvanceListener.js).
//
//   welcome      first visit to the workspace - the tabs, a plan and its missions, how to make your own
//   new-plan     the first time "New plan" opens - the three sources, the chat, the draft
//   path-review  the first time a mission's design opens for review - plain words, details, approve
import React from 'react';

const Box = ({ title, children, wide }) => (
  <div style={{ maxWidth: wide ? 340 : 290 }}>
    {title && <h3 style={{ margin: 0, marginBottom: 6 }}>{title}</h3>}
    {children}
  </div>
);
const Hint = ({ children }) => <p style={{ fontSize: 13, color: '#555', marginBottom: 0 }}>{children}</p>;

const MISSION = '[data-tour^="gb-mission-"]';

export const TOURS = {
  welcome: ({ hasPlans, isDemo, isOwner }) => [
    {
      selector: 'body',
      position: 'center',
      content: () => (
        <Box wide title="👋 Welcome to SimulateJob">
          <p>Turn a procedure - an SOP, a manual, a policy - into a hands-on game your people play in the browser:
            they walk into a scene, spot what&rsquo;s wrong and put it right.</p>
          {isDemo && hasPlans && <p><strong>Your demo already has a sample plan with its games built,</strong> so you can look around without waiting for the AI.</p>}
          {isDemo && <p style={{ fontSize: 13 }}>Demo data is deleted after the session. Please don&rsquo;t upload confidential documents.</p>}
          <Hint>Click <strong>Next</strong> for a two-minute tour.</Hint>
        </Box>
      ),
    },
    {
      selector: '#studio',
      position: 'right',
      advanceOn: '#studio',
      hideFooter: true,
      content: () => (
        <Box title="My Simulations">
          <p>This is where simulations are made.</p>
          <Hint>Click <strong>My Simulations</strong> to continue.</Hint>
        </Box>
      ),
    },
    ...(hasPlans ? [
      {
        selector: '[data-tour="gb-plans"]',
        position: 'right',
        content: () => (
          <Box title="Plans">
            <p>A plan is one procedure turned into training: its goal, who plays, and a few <strong>missions</strong>.
              {isOwner ? ' As an owner you see every plan in your organization.' : ' You see the plans you made.'}</p>
          </Box>
        ),
      },
      {
        selector: MISSION,
        position: 'top',
        content: () => (
          <Box title="A mission">
            <p>One scene the player walks into. Its chip shows how far it has got:</p>
            <p style={{ fontSize: 13 }}><strong>Not built</strong> → <strong>Design to review</strong> → <strong>Mission ready</strong> → <strong>Game ready</strong></p>
          </Box>
        ),
      },
      {
        selector: `${MISSION} [data-tour="gb-steps"]`,
        position: 'top',
        content: () => (
          <Box title="Its stages">
            <p><strong>Design › Build › Share</strong>: the AI designs it and you approve it, then the scene is painted, then you
              try it and share a link. Done stages show ✓ - click one to see what was made there and change it.</p>
          </Box>
        ),
      },
      {
        selector: `${MISSION} [data-tour="gb-next"]`,
        position: 'top',
        content: () => (
          <Box title="What to do next">
            <p>One button, always the next thing for this mission - <em>Design with AI</em>, <em>Paint the game</em>,
              <em> ▶ Play-test</em>... Slow ones run in the background - about 20 seconds for words, a minute for pictures.
              Learners play the shared link without an account.</p>
          </Box>
        ),
      },
    ] : [
      {
        selector: '[data-tour="gb-empty"]',
        position: 'bottom',
        content: () => (
          <Box title="No plans yet">
            <p>Your first plan starts from a document, a web page, or a short chat about the job.</p>
          </Box>
        ),
      },
    ]),
    {
      selector: '[data-tour="gb-new-plan"]',
      position: 'bottom',
      content: () => (
        <Box title="Make your own">
          <p><strong>＋ New plan</strong> starts from your SOP. The studio asks only what the document can&rsquo;t tell it,
            then drafts the missions for you to check.</p>
        </Box>
      ),
    },
    {
      selector: '[data-tour="gb-inventory"]',
      position: 'bottom',
      content: () => (
        <Box title="Item inventory">
          <p>The things a scene can hold - doors, cabinets, extinguishers, logbooks... - each with what it can really do.
            Missions are built from these, so a game can always be won.</p>
        </Box>
      ),
    },
    ...(isOwner && !isDemo ? [{
      selector: '#settings',
      position: 'right',
      content: () => (
        <Box title="Your team">
          <p>Invite colleagues under <strong>Settings → Members</strong>. Creators build games; owners also manage the team
            and see every plan.</p>
        </Box>
      ),
    }] : []),
    {
      selector: 'body',
      position: 'center',
      content: () => (
        <Box wide title="✅ That's the tour">
          <p>{hasPlans ? 'Open a mission and press ▶ Play to try it as a learner would.' : 'Start with ＋ New plan and your own procedure.'}</p>
          <Hint>Walkthroughs for New plan and design review start by themselves the first time. Replay any of them from the
            menu under your avatar.</Hint>
        </Box>
      ),
    },
  ],

  'new-plan': () => [
    {
      selector: '[data-tour="np-sources"]',
      position: 'bottom',
      content: () => (
        <Box title="1. Where it starts">
          <p>Upload your procedure (.pdf, .md or .txt), fetch a web page, or just describe the job in a chat.
            Documents stay in your organization.</p>
        </Box>
      ),
    },
    {
      selector: '[data-tour="np"]',
      position: 'top',
      content: () => (
        <Box title="2. A short chat">
          <p>The studio asks only what the document can&rsquo;t tell it: who plays and how experienced they are,
            what they must do, and the real mistakes people make. Difficulty and the number of missions are decided for you.</p>
        </Box>
      ),
    },
    {
      selector: '[data-tour="np"]',
      position: 'top',
      content: () => (
        <Box title="3. The draft">
          <p><strong>Draft the missions</strong> writes the tasks and missions (about 20 seconds). Check them, then
            <strong> Save plan</strong> - or <em>Change something</em> and tell the chat what.</p>
        </Box>
      ),
    },
  ],

  'path-review': ({ plain }) => [
    {
      selector: 'body',
      position: 'center',
      content: () => (
        <Box wide title="Review the design">
          <p>Before anything is built you check how the mission is won. Nothing here calls the AI - edits are checked by code as you make them.</p>
        </Box>
      ),
    },
    ...(plain ? [
      {
        selector: '[data-tour="pe-plain"]',
        position: 'top',
        content: () => (
          <Box title="In plain words">
            <p>What the player finds and what they must do. If this is wrong for your workplace, the game will be too.</p>
          </Box>
        ),
      },
      {
        selector: '[data-tour="pe-details"]',
        position: 'top',
        advanceOn: '[data-tour="pe-details"]',
        hideFooter: true,
        waitMs: 3000,
        content: () => (
          <Box title="Every detail">
            <p>To change something, open the details.</p>
            <Hint>Click <strong>Show every detail</strong> to continue.</Hint>
          </Box>
        ),
      },
    ] : []),
    {
      selector: '[data-tour="pe-room"]',
      position: 'top',
      content: () => (
        <Box title="In the room">
          <p>Every object in the scene and how it starts. Each can only do what its item type allows - pick from the lists.</p>
        </Box>
      ),
    },
    {
      selector: '[data-tour="pe-end"]',
      position: 'top',
      content: () => (
        <Box title="How it must end">
          <p>The state each thing must be in once the job is done right. A line in red is something code found -
            like a lock with no key in the room.</p>
        </Box>
      ),
    },
    {
      selector: '[data-tour="pe-approve"]',
      position: 'top',
      content: () => (
        <Box title="Approve">
          <p><strong>Approve design</strong> writes the words (one AI call) and code builds the mission.
            Then <em>Paint the game</em> draws the scene.</p>
        </Box>
      ),
    },
  ],
};

export const TOUR_LABELS = { welcome: 'Welcome tour', 'new-plan': 'New plan', 'path-review': 'Reviewing a design' };
